import React, { useState } from 'react';
import { Box, Text, Button, Input, InputGroup, InputRightElement } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useSupabase } from '../contexts/SupabaseContext';
import { ROUTES } from '../constants';



const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const { handlePasswordReset } = useSupabase();

  // Email validation function
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check if send button should be enabled
  const isSendEnabled = email && isValidEmail(email);

  const handleSendResetLink = async () => {
    if (!isSendEnabled) return;

    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await handlePasswordReset(email);
      setSuccessMessage('Password reset link sent! Please check your email.');
      // Clear email after successful send
      setTimeout(() => {
        setEmail('');
      }, 2000);
    } catch (error: any) {
      console.error('Password reset error:', error);
      setErrorMessage(error.message || 'Failed to send password reset link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate(ROUTES.LOGIN);
  };

  return (
    <Box
    className="base-container"
    >
 
      {/* Main Content */}
      <Box
        className="wizard-main-content"
      >
        {/* Title - positioned at y: 124 */}
        <Box
          position="absolute"
          top="124px"
          left="20px"
          width="335px"
        >
          <Text
            className="title-text"
          >
            Enter your email
          </Text>
        </Box>

        {/* Description text - positioned at y: 164 */}
        <Box
          position="absolute"
          top="164px"
          left="20px"
          width="335px"
        >
          <Text
            className="static-text"
          >
            You will receive a link to create a new password
          </Text>
        </Box>

        {/* Error Message */}
        {errorMessage && (
          <Box
            position="absolute"
            top="200px"
            left="20px"
            width="335px"
          >
            <Text
              className="error-message"
            >
              {errorMessage}
            </Text>
          </Box>
        )}

        {/* Success Message */}
        {successMessage && (
          <Box
            position="absolute"
            top="200px"
            left="20px"
            width="335px"
          >
            <Text
              color="green.400"
              fontSize="14px"
            >
              {successMessage}
            </Text>
          </Box>
        )}

        {/* Email Input - positioned at y: 232 */}
        <Box
          position="absolute"
          top="232px"
          left="22px"
          width="333px"
          height="44px"
        >
          <InputGroup>
            <Input
              className="input-field"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <InputRightElement
              position="absolute"
              left="0"
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="none"
            >
              <svg width="19" height="16" viewBox="0 0 19 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.9725 2.66667C17.9725 1.75 17.2157 1 16.2907 1H2.83612C1.91112 1 1.1543 1.75 1.1543 2.66667M17.9725 2.66667V12.6667C17.9725 13.5833 17.2157 14.3333 16.2907 14.3333H2.83612C1.91112 14.3333 1.1543 13.5833 1.1543 12.6667V2.66667M17.9725 2.66667L9.56339 8.5L1.1543 2.66667" stroke="#90909A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </InputRightElement>
          </InputGroup>
        </Box>

        {/* Send Reset Link Button - positioned at y: 316 */}
        <Box
          className="button-big-container"
          top="316px"
        >
          <Button
            className="button-big"
            onClick={handleSendResetLink}
            isDisabled={!isSendEnabled || isLoading}
            isLoading={isLoading}
            loadingText="Sending..."
          >
            Send reset link
          </Button>
        </Box>

        {/* Back to Login Link - positioned 30px from bottom */}
        <Box
          position="absolute"
          bottom="30px"
          left="20px"
          width="335px"
        >
          <Text
            className='regular-text'
            textAlign="center"
            onClick={handleBackToLogin}
          >
            Back to Login
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;
